<template>
  <v-menu location="bottom end" :close-on-content-click="false">
    <template #activator="{props}">
      <!-- O contador no rótulo: uma lista filtrada tem a mesma aparência de uma lista
           inteira, e todas estas escolhas ficam guardadas entre visitas. Abaixo do md o botão é
           só ícone, e os números vão para o title. -->
      <ontos-header-action :icon="icon" :label="label" :title="summary" v-bind="props">
        {{ label }}&nbsp;<ontos-count :count="modelValue.length" :hidden="hiddenCount"/>
      </ontos-header-action>
    </template>
    <!-- py-0: o padding vertical do v-list aparece como faixa branca acima da primeira
         linha e abaixo da última quando os itens têm cor de fundo própria. -->
    <v-list density="compact" class="py-0">
      <!-- "Todos" no topo, com a mesma caixa e o mesmo gesto das demais linhas: marcar tudo é o
           caminho de volta depois de esconder uma por uma, e sem ele são N cliques.

           `indeterminate` quando só parte está marcada, que é o desenho nativo do Vuetify para
           isto — a caixa diz em que estado a lista está, e não só o que o clique vai fazer.

           Trava quando tudo está marcado E há mínimo: ali o clique só poderia desmarcar tudo, e
           é justamente o que o mínimo proíbe. É a mesma regra de linha travada das demais, pelo
           mesmo motivo — botão que aceita clique sem efeito é pior do que botão desligado. -->
      <v-list-item class="check-menu-option" :disabled="allLocked" @click="toggleAll">
        <template #prepend>
          <v-list-item-action start>
            <v-checkbox-btn :model-value="allChosen" :indeterminate="someChosen"
                            :disabled="allLocked"/>
          </v-list-item-action>
        </template>
        <span class="font-weight-medium">{{ $tt('all') }}</span>
      </v-list-item>
      <v-divider/>

      <v-list-item v-for="item of items" :key="item.value"
                   :class="['check-menu-option', {'check-menu-option--tinted': !!hoverColor}, item.class]"
                   :style="hoverColor ? {'--check-menu-hover': hoverColor} : undefined"
                   :disabled="isLocked(item)"
                   @click="toggle(item.value)">
        <!-- A caixa no `prepend` e o nome no conteúdo, que é o arranjo nativo de lista marcável
             do Vuetify. Antes a linha era um `v-checkbox` inteiro com o nome dentro do rótulo
             dele; rótulo cola no controle, então o nome ficava rente à caixa — e, quando ele
             passou a vir com marca própria (a régua de um item de coleção), a marca ficava mais
             perto da caixa do que do nome a que pertence.

             O `v-list-item-action` NÃO é embrulho à toa, e foi preciso ler o CSS da biblioteca
             para descobrir: o espaço do `prepend` é um `.v-list-item__spacer` cuja largura
             depende DO QUE está dentro — 32px depois de um `v-icon`, 16px depois de um
             `v-avatar`, e para qualquer outro elemento uma variável SEM valor padrão, que
             resolve em zero. Era por isso que a caixa ficava colada. O `v-list-item-action` é a
             peça que o Vuetify dá para controle em linha de lista, e traz o par certo: 8px
             depois e -8px antes, que absorvem o recuo interno do próprio controle.

             `v-checkbox-btn`, e não `v-checkbox`: é a caixa sem o invólucro de campo de
             formulário, que trazia altura e a linha de mensagem que nunca aparece — era o que o
             `density` e o `hide-details` estavam ali para desfazer.

             Sem handler próprio, de propósito: o clique dela borbulha para a linha, que é quem
             alterna. Com um handler, alternaria duas vezes. -->
        <template #prepend>
          <v-list-item-action start>
            <v-checkbox-btn :model-value="modelValue.includes(item.value)"
                            :color="item.color" :disabled="isLocked(item)"/>
          </v-list-item-action>
        </template>
        <!-- Com `option`, o nome sai na FORMA do valor — régua, bolinha ou chip —, que é como
             ele aparece no resto do app. Sem ela é texto, que é o que os menus de campos e de
             colunas querem: ali o item é um nome de propriedade, e não um valor colorido. -->
        <ontos-colored-value v-if="item.option && item.form"
                             :option="item.option" :form="item.form"/>
        <span v-else :class="item.labelClass">{{ item.title }}</span>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import Count from '@/components/shared/Count.vue'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import {ColoredOption, ValueForm} from '@/types/ColoredOption'

/**
 * O valor identifica o item na lista marcada. Texto ou número: os campos e as colunas são
 * alias de propriedade, as raias são id de situação.
 */
export type CheckValue = string | number

export type CheckMenuItem = {
  value: CheckValue
  title: string
  /** Classe da LINHA — um fundo, tipicamente. */
  class?: string
  /** Cor da caixa de seleção. */
  color?: string
  /** Classe do nome. */
  labelClass?: string
  /**
   * O valor que esta linha representa, quando ela representa um — a raia de um Kanban.
   *
   * Vem junto com a `form` porque as duas andam sempre: desenhar o valor exige saber qual é e
   * de que natureza ele é. Ausentes, o item é só um nome, que é o caso dos menus de campos e
   * de colunas.
   */
  option?: ColoredOption
  form?: ValueForm
}

/**
 * Menu de opções marcáveis num botão de cabeçalho: quais campos, quais colunas, quais
 * raias.
 *
 * Nasceu de três cópias que já tinham divergido entre si — a das colunas da listagem
 * ficara sem `density` e sem `hide-details`, e por isso cada caixa reservava a linha de
 * mensagem que nunca aparece, espaçando os itens ao dobro dos outros dois menus.
 *
 * **O clique é da LINHA, e não só da caixa.** O `v-list-item` do Vuetify só destaca no
 * hover quando é interativo, então um item passivo com uma caixa dentro não reage ao
 * ponteiro — e uma linha que destaca sem responder é pior do que uma que não destaca.
 * A caixa fica só com `:model-value`, sem handler próprio: o clique dela borbulha para a
 * linha, senão alternaria duas vezes.
 *
 * **A ordem de saída é a dos itens, não a de clique.** Era um invariante repetido nos
 * handlers do Kanban; agora é garantido aqui, uma vez.
 *
 * O `min` trava as últimas opções marcadas em vez de desfazer o clique depois — e trava
 * a linha junto, senão ela continuaria destacando e aceitando um clique sem efeito.
 */
@Component({
  name: 'ontos-check-menu',
  emits: ['update:modelValue'],
  components: {'ontos-count': Count, 'ontos-colored-value': ColoredValue}
})
class CheckMenu extends Vue {
  @Prop({type: String, required: true}) readonly icon!: string
  @Prop({type: String, required: true}) readonly label!: string
  @Prop({type: Array, required: true}) readonly items!: CheckMenuItem[]
  @Prop({type: Array, required: true}) readonly modelValue!: CheckValue[]
  /** Quantas opções no mínimo ficam marcadas. Zero deixa desmarcar todas. */
  @Prop({type: Number, default: 0}) readonly min!: number
  /**
   * Cor do hover, quando o padrão não serve.
   *
   * A camada de hover do Vuetify ESCURECE o que estiver embaixo — sobre linhas de cores
   * diferentes ela dá um cinza por linha, em vez de um destaque só. Com uma cor aqui,
   * ela é desligada e o fundo é pintado direto.
   */
  @Prop({type: String, default: ''}) readonly hoverColor!: string

  /** Nunca negativo: o modelo pode trazer um valor que saiu das opções. */
  get hiddenCount(): number {
    return Math.max(0, this.items.length - this.modelValue.length)
  }

  /** O mesmo par de números do botão, para o tooltip do xs, onde o rótulo some. */
  get summary(): string {
    const hidden = this.hiddenCount
    return `${this.label} (${this.modelValue.length}${hidden ? '+' + hidden : ''})`
  }

  get allChosen(): boolean {
    return this.items.length > 0 && this.modelValue.length >= this.items.length
  }

  /** Nem tudo nem nada: é o estado em que a caixa de "Todos" fica indeterminada. */
  get someChosen(): boolean {
    return this.modelValue.length > 0 && !this.allChosen
  }

  /** Com tudo marcado só restaria desmarcar tudo, e é isso que o mínimo proíbe. */
  get allLocked(): boolean {
    return this.allChosen && this.min > 0
  }

  toggleAll() {
    if (this.allLocked) return
    this.$emit('update:modelValue', this.allChosen ? [] : this.items.map(item => item.value))
  }

  isLocked(item: CheckMenuItem): boolean {
    return this.modelValue.length <= this.min && this.modelValue.includes(item.value)
  }

  toggle(value: CheckValue) {
    const chosen = this.modelValue.includes(value)
        ? this.modelValue.filter(current => current !== value)
        : [...this.modelValue, value]
    this.$emit('update:modelValue', this.items
        .map(item => item.value)
        .filter(candidate => chosen.includes(candidate)))
  }
}

export default toNative(CheckMenu)
</script>

<style scoped>
.check-menu-option--tinted:hover {
  background-color: var(--check-menu-hover);
}

.check-menu-option--tinted :deep(.v-list-item__overlay) {
  opacity: 0;
}
</style>
